/**
 * Uygulama genelinde paylaşılan sabitler ve enum değerleri.
 * Doğruluk kaynağı: docs/03-veritabani-semasi.md
 *
 * NOT: Buradaki değerler Prisma enum'larıyla BİREBİR aynı olmalıdır.
 * Kullanıcıya gösterilen Türkçe etiketler burada DEĞİL, web/src/i18n/tr.json içindedir.
 *
 * Her enum önce bir `... as const` dizi olarak tanımlanır (zod `z.enum()` ve döngüler için),
 * ardından `enumFrom` ile nokta erişimli nesneye dönüştürülür.
 */
/** `['A','B']` → `{ A: 'A', B: 'B' }` */
function enumFrom(values) {
    return Object.fromEntries(values.map((value) => [value, value]));
}
// ---------------------------------------------------------------------------
// Kullanıcı ve firma
// ---------------------------------------------------------------------------
/** Platform rolü. Firma rolü (satıcı/alıcı) ayrı: CompanyType. */
export const USER_ROLES = ['ADMIN', 'MEMBER'];
export const UserRole = enumFrom(USER_ROLES);
export const COMPANY_TYPES = ['SELLER', 'BUYER', 'BOTH', 'CARRIER'];
export const CompanyType = enumFrom(COMPANY_TYPES);
/** Firma, ilan ve yorum onaylarında ortak kullanılır. */
export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const ApprovalStatus = enumFrom(APPROVAL_STATUSES);
// ---------------------------------------------------------------------------
// İlan
// ---------------------------------------------------------------------------
export const LISTING_STATUSES = [
    'DRAFT',
    'PENDING',
    'ACTIVE',
    'PAUSED',
    'REJECTED',
    'EXPIRED',
    'DELETED',
];
export const ListingStatus = enumFrom(LISTING_STATUSES);
/** FIXED: birim fiyat girilir. NEGOTIABLE: "teklife açık", fiyat gizlenir. */
export const PRICE_TYPES = ['FIXED', 'NEGOTIABLE'];
export const PriceType = enumFrom(PRICE_TYPES);
export const UNITS = ['TON', 'KG', 'M3', 'ADET'];
export const Unit = enumFrom(UNITS);
export const RECURRENCES = ['ONE_TIME', 'WEEKLY', 'MONTHLY', 'CONTINUOUS'];
export const Recurrence = enumFrom(RECURRENCES);
export const DELIVERY_OPTIONS = ['SELLER_DELIVERS', 'BUYER_PICKS_UP', 'NEGOTIABLE'];
/**
 * Ürünün TAŞINDIĞI form (müşteri Excel'i: "Taşıma Formu").
 *
 * Küçük balya listede yok — müşterinin ürün tablosunda geçmiyor, mevcut iki ilan
 * büyük balyaya taşındı.
 */
export const DELIVERY_FORMS = ['BUYUK_BALYA', 'DOKME', 'BIG_BAG'];
/**
 * Ürünün FİZİKSEL yapısı (müşteri Excel'i: "Form / Fiziksel Yapı").
 *
 * Taşıma formundan ayrıdır: balya bir tek şekilde taşınır ama dökme bir ürün
 * hem big bag hem dökme gidebilir.
 */
export const PHYSICAL_FORMS = ['BALYA', 'DOKME', 'KURUTULMUS_PELET'];
export const PhysicalForm = enumFrom(PHYSICAL_FORMS);
/** Taşıma aracı ve boşaltma ekipmanı (müşteri Excel'i, 2. sayfa). */
export const VEHICLES = ['DAMPER', 'ACIK_TIR'];
export const UNLOADERS = ['DAMPER', 'KONVEYOR', 'FORKLIFT', 'KEPCE', 'PNOMATIK'];
/**
 * Taşıma formu → araç ve boşaltma ekipmanı.
 *
 * Bu eşleme müşterinin Excel'inden birebir geliyor ve ürüne değil FORMA bağlı;
 * o yüzden veritabanında değil burada duruyor. Alıcı "tesisimde forklift var"
 * dediğinde hangi ürünleri boşaltabileceği buradan çıkıyor.
 */
export const TRANSPORT_PROFILE = {
    BIG_BAG: { vehicle: 'DAMPER', unloaders: ['DAMPER', 'KONVEYOR'] },
    BUYUK_BALYA: { vehicle: 'ACIK_TIR', unloaders: ['FORKLIFT', 'KONVEYOR'] },
    DOKME: { vehicle: 'DAMPER', unloaders: ['KEPCE', 'KONVEYOR', 'PNOMATIK'] },
};
export const DeliveryForm = enumFrom(DELIVERY_FORMS);
export const DeliveryOption = enumFrom(DELIVERY_OPTIONS);
// ---------------------------------------------------------------------------
// Teklif (RFQ)
// ---------------------------------------------------------------------------
export const OFFER_STATUSES = [
    'PENDING',
    'COUNTERED',
    'ACCEPTED',
    'REJECTED',
    'EXPIRED',
    'CANCELLED',
];
export const OfferStatus = enumFrom(OFFER_STATUSES);
/** Pazarlık geçmişindeki (OfferRevision) hareket türü. */
export const OFFER_ACTIONS = ['OFFER', 'COUNTER', 'ACCEPT', 'REJECT'];
export const OfferAction = enumFrom(OFFER_ACTIONS);
// ---------------------------------------------------------------------------
// Sipariş ve ödeme
// ---------------------------------------------------------------------------
/**
 * Siparişe bağlı sevkiyatın ALICIYA gösterilen statüsü (müşteri, 17.09.2026):
 *
 *   YOLDA ──(kantar fişi)──> TAMAMLANDI
 *
 * Sevkiyat tablosundaki ayrıntılı statünün (docs/11 §2.5) alıcı dilindeki karşılığı:
 * teslim edilmiş sevkiyat TAMAMLANDI, iptal olmayan diğer her şey YOLDA.
 */
export const ORDER_SHIPMENT_STATUSES = ['YOLDA', 'TAMAMLANDI'];
export const OrderShipmentStatus = enumFrom(ORDER_SHIPMENT_STATUSES);
export const PAYMENT_STATUSES = [
    'INITIATED',
    'SUCCESS',
    'FAILED',
    'REFUNDED',
    'PARTIAL_REFUND',
];
export const PaymentStatus = enumFrom(PAYMENT_STATUSES);
// ---------------------------------------------------------------------------
// Bildirim
// ---------------------------------------------------------------------------
export const NOTIFICATION_TYPES = [
    'OFFER_NEW',
    'OFFER_REPLY',
    'MESSAGE_NEW',
    'LISTING_APPROVED',
    'LISTING_REJECTED',
    'ORDER_STATUS',
    'REVIEW_NEW',
    /// Kayıtlı aramaya uyan yeni ilan (docs/09 §1.6).
    'SAVED_SEARCH_MATCH',
    /// Satıcının ürün türüne uyan yeni alım talebi (docs/10 §1).
    'DEMAND_MATCH',
    /// Alıcının talebine yeni teklif geldi (docs/10 §1).
    'DEMAND_BID',
    /// Numune talebi ya da durum değişikliği (docs/10 §2).
    'SAMPLE_UPDATE',
    /// Taşıyıcıya uygun yeni sevkiyat ilanı (docs/11 §2).
    'SHIPMENT_NEW',
    /// Sevkiyat başvurusu, atama ya da durum değişikliği (docs/11 §2).
    'SHIPMENT_UPDATE',
    'SYSTEM',
];
export const NotificationType = enumFrom(NOTIFICATION_TYPES);
// ---------------------------------------------------------------------------
// İş kuralları (docs/01 §7, docs/02)
// ---------------------------------------------------------------------------
/** Varsayılan platform komisyonu (%). Admin panelinden Setting ile değiştirilir. */
export const DEFAULT_COMMISSION_RATE = 5;
/** Varsayılan KDV oranı (%). Kategori bazında geçersiz kılınabilir. */
export const DEFAULT_VAT_RATE = 20;
/** İlan yayın süresi (gün). */
export const LISTING_DURATION_DAYS = 60;
/** Teklif geçerlilik süresi (gün). */
export const OFFER_VALIDITY_DAYS = 7;
/** Teslim onayı gelmezse siparişin otomatik tamamlanma süresi (gün). */
export const ORDER_AUTO_COMPLETE_DAYS = 14;
/** İlan başına görsel sayısı sınırları. */
export const LISTING_IMAGE_MIN = 1;
export const LISTING_IMAGE_MAX = 8;
/** Yükleme sınırları (bayt). */
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;
export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ALLOWED_DOCUMENT_MIME_TYPES = ['application/pdf'];
/** Listeleme uçlarında sayfalama varsayılanları (docs/04: `?page=1&limit=20`, limit ≤ 50). */
export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 50;
/** Para birimi — V1'de yalnızca TRY. */
export const CURRENCY = 'TRY';
// ---------------------------------------------------------------------------
// Oturum ve token süreleri (docs/02 §3)
// ---------------------------------------------------------------------------
export const ACCESS_TOKEN_TTL_MINUTES = 15;
export const REFRESH_TOKEN_TTL_DAYS = 30;
export const EMAIL_VERIFICATION_TTL_HOURS = 24;
export const PASSWORD_RESET_TTL_HOURS = 1;
/** Cookie adları — web tarafı bunları okumaz (httpOnly), yalnızca CSRF cookie'si okunur. */
export const ACCESS_TOKEN_COOKIE = 'at';
export const REFRESH_TOKEN_COOKIE = 'rt';
export const CSRF_COOKIE = 'csrf';
export const CSRF_HEADER = 'x-csrf-token';
// ---------------------------------------------------------------------------
// Setting anahtarları
// ---------------------------------------------------------------------------
export const SETTING_KEYS = [
    'commission_rate',
    'default_vat_rate',
    'contact_info',
    'announcement_banner',
    'shipment_track_radius_km',
];
export const SettingKey = {
    COMMISSION_RATE: 'commission_rate',
    DEFAULT_VAT_RATE: 'default_vat_rate',
    CONTACT_INFO: 'contact_info',
    ANNOUNCEMENT_BANNER: 'announcement_banner',
    /** Alıcıya canlı takibin açıldığı yarıçap (km) — docs/12. */
    SHIPMENT_TRACK_RADIUS_KM: 'shipment_track_radius_km',
};
// ---------------------------------------------------------------------------
// API hata kodları (docs/02 §4)
// ---------------------------------------------------------------------------
export const ErrorCode = {
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    // Kimlik doğrulama
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    EMAIL_TAKEN: 'EMAIL_TAKEN',
    EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
    COMPANY_NOT_APPROVED: 'COMPANY_NOT_APPROVED',
    ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
    INVALID_TOKEN: 'INVALID_TOKEN',
    CSRF_FAILED: 'CSRF_FAILED',
    // Alan kuralları
    UPLOAD_INVALID: 'UPLOAD_INVALID',
    INVALID_TRANSITION: 'INVALID_TRANSITION',
};
// ---------------------------------------------------------------------------
// Alım talebi (docs/10 §1)
// ---------------------------------------------------------------------------
export const DEMAND_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'CLOSED', 'EXPIRED'];
export const DemandStatus = enumFrom(DEMAND_STATUSES);
/** Talep listesi sıralaması. `distance`: satıcının konumuna yakınlık. */
export const DEMAND_SORTS = ['newest', 'quantity', 'distance', 'expiring'];
/** Talebin süresi — alıcı aksini seçmezse bu kadar gün açık kalır. */
export const DEMAND_VALIDITY_DAYS = 30;
// ---------------------------------------------------------------------------
// Numune ve analiz (docs/10 §2)
// ---------------------------------------------------------------------------
export const SAMPLE_STATUSES = [
    'TALEP_EDILDI',
    'GONDERILDI',
    'TESLIM_ALINDI',
    'SONUCLANDI',
    'IPTAL',
];
export const SampleStatus = enumFrom(SAMPLE_STATUSES);
export const SAMPLE_DELIVERIES = ['ELDEN', 'KARGO'];
export const SampleDelivery = enumFrom(SAMPLE_DELIVERIES);
export const SAMPLE_VERDICTS = ['UYGUN', 'SARTLI', 'UYGUN_DEGIL'];
export const SampleVerdict = enumFrom(SAMPLE_VERDICTS);
// ---------------------------------------------------------------------------
// Mobil istemci (docs/11)
// ---------------------------------------------------------------------------
/**
 * İstemci türünü bildiren başlık. Değeri `mobile` olan istekler jetonları YANIT
 * GÖVDESİNDE de alır; web almaz, çünkü orada jeton httpOnly çerezde durur ve
 * JavaScript'in eline geçmemesi XSS'e karşı tek savunma.
 */
export const CLIENT_HEADER = 'x-client';
export const MOBILE_CLIENT = 'mobile';
// ---------------------------------------------------------------------------
// Sevkiyat ve taşıyıcı (docs/11 §2)
// ---------------------------------------------------------------------------
export const SHIPMENT_STATUSES = ['YAYINDA', 'ATANDI', 'YOLDA', 'TESLIM_EDILDI', 'IPTAL'];
export const ShipmentStatus = enumFrom(SHIPMENT_STATUSES);
export const VEHICLE_TYPES = ['TIR', 'KAMYON', 'KAMYONET', 'DAMPERLI', 'SILOBAS'];
export const VehicleType = enumFrom(VEHICLE_TYPES);
export const APPLICATION_STATUSES = ['BEKLIYOR', 'KABUL', 'RED', 'GERI_CEKILDI'];
export const ApplicationStatus = enumFrom(APPLICATION_STATUSES);
/**
 * Konum bildirimi arasındaki en kısa süre (saniye). Cihaz bundan sık gönderse de
 * sunucu fazlasını yazmaz: pil ve veritabanı boşuna tüketilmesin.
 */
export const LOCATION_MIN_INTERVAL_SECONDS = 20;
/** Tek istekte gönderilebilecek en fazla konum noktası (çevrimdışı biriken iz). */
export const LOCATION_BATCH_MAX = 100;
/**
 * Ham konum izinin saklanma süresi (gün). Teslimden sonra bu süre geçince iz silinir;
 * kalıcı olarak yalnız sevkiyatın son noktası ve zaman damgaları kalır (KVKK).
 */
export const LOCATION_RETENTION_DAYS = 30;
//# sourceMappingURL=constants.js.map